import {BaseComponent} from '../base/component.base.js';
import template from './notification.template.html';
import styles from './notification.styles.css';

/** Shortest time a non-error message stays visible. */
const BASE_DURATION_MS = 5000;
/** Longest time a non-error message stays visible, however long it is. */
const MAX_DURATION_MS = 8000;
/** Characters a message may have before it earns extra reading time. */
const BASE_LENGTH = 80;
/** Characters per extra second of reading time beyond `BASE_LENGTH`. */
const CHARS_PER_EXTRA_SECOND = 30;

/**
 * @param {string} message
 * @returns {number} milliseconds a message of this length stays visible
 */
export function readingDurationMs(message) {
    const extra = Math.max(0, Math.floor((message.length - BASE_LENGTH) / CHARS_PER_EXTRA_SECOND)) * 1000;
    return Math.min(MAX_DURATION_MS, BASE_DURATION_MS + extra);
}

export class NotificationComponent extends BaseComponent {
    #timeout = null;

    /**
     * Escape dismisses the message on screen, wherever focus is. It listens on the window, after
     * every document listener, so a handler that takes the same Escape (and cancels the event)
     * keeps it, and an open dialog, which Escape closes, keeps it to itself.
     * @param {KeyboardEvent} e
     */
    #onKeydown = (e) => {
        if (e.key !== 'Escape' || e.defaultPrevented || this.#visibleType() === null) return;
        if (e.composedPath().some((node) => node instanceof HTMLDialogElement && node.open)) return;
        this.hide();
    };

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#btn-dismiss').addEventListener('click', () => this.hide());
        window.addEventListener('keydown', this.#onKeydown);
    }

    disconnectedCallback() {
        window.removeEventListener('keydown', this.#onKeydown);
        this.hide();
    }

    /**
     * Shows a message. Error messages stay until dismissed, since they often carry details (such as
     * a template syntax error with its line number) that take longer to read. Other messages hide
     * after a time that grows with their length, unless `durationMs` sets it.
     *
     * @param {string} message
     * @param {'success' | 'error' | 'warning'} type
     * @param {{durationMs?: number}} [options]
     */
    show(message, type, options = {}) {
        const toast = this.$('#toast');
        const msg = this.$('#message');
        if (!toast || !msg) return;

        clearTimeout(this.#timeout);
        this.#timeout = null;
        toast.className = type;
        msg.textContent = message;

        const durationMs = options.durationMs ?? (type === 'error' ? null : readingDurationMs(message));
        if (durationMs !== null) {
            this.#timeout = setTimeout(() => this.hide(), durationMs);
        }
    }

    /**
     * Hides the message when it is an error, and leaves any other message alone. Driving adapters
     * call this once a later request succeeds, so an error about an earlier action does not stay
     * on screen after the app works again.
     */
    clearError() {
        if (this.#visibleType() === 'error') {
            this.hide();
        }
    }

    /** @returns {string | null} type of the message on screen, or null when none is shown */
    #visibleType() {
        const type = this.$('#toast')?.className;
        return type && type !== 'hidden' ? type : null;
    }

    hide() {
        clearTimeout(this.#timeout);
        this.#timeout = null;
        const toast = this.$('#toast');
        if (toast) {
            toast.className = 'hidden';
        }
    }
}

customElements.define('admin-notification', NotificationComponent);
